/**
 * [MOCK] Placeholder content for the AdvicePrep assistant.
 *
 * UI-first build: everything here is static so the panel can be designed and
 * reviewed before any of it exists on the backend. Each block notes the API
 * that replaces it. Per REACT-CODEGEN-RULES.md §3.2, mock and real behaviour
 * are not mixed — the Analysis tab talks to the real deep-dive endpoint and
 * nothing in this file feeds it.
 *
 * Pop-ups and feedback have MIGRATED off this file — their endpoints shipped, so
 * the mocks were removed rather than left beside the real thing
 * (REACT-CODEGEN-RULES.md §7.2). See src/api/advisoryPopups.ts and
 * src/api/advisoryFeedback.ts.
 *
 * What remains here is genuinely not API-backed:
 *   tickets   → [MOCK] awaiting GET support/ui/tickets. Submitting already has
 *               a real endpoint (submitSupportForm in src/api/support.ts) but is
 *               left unwired on purpose — it emails a human.
 *   features  → the six ratable areas. Client-owned copy by design, per
 *               PREPPY-FEEDBACK-SPEC.md §4; the keys are sent, the wording is not.
 *   guides    → ship in-bundle; they are code, not content
 *   articles  → external Help Centre links only
 */

import type { Guide, RatableFeature, TicketStatusValue } from './types'

/** [MOCK] Move to src/config/featureFlags.ts as an env-backed value. */
/**
 * The Intercom-hosted Help Centre.
 *
 * Only used for "browse everything" links. Per-article URLs are **not** built
 * from this — they come from Intercom in the payload, because its URLs carry
 * the article id and a locale segment and constructing one by hand is a guess
 * that rots the moment a title changes.
 */
export const HELP_CENTRE_URL = 'https://support.dashboardinsights.com/en/'

/* ── wizard steps, named so guides don't hardcode integers ───────────────── */

export const PREP_STEP = {
  agenda: 0,
  data: 1,
  report: 2,
  outputs: 3,
} as const

/* ── guides ─────────────────────────────────────────────────────────────── */

/**
 * A short list of walkthroughs, deliberately. Anything that does not need to
 * point at the screen is a Help Centre article instead — one place to author
 * help. Adding one is a product decision, not a content one.
 *
 * `anchor` values must match a `data-tour` attribute on a real element.
 */
export const GUIDES: Guide[] = [
  /**
   * The app tour. The only guide that crosses screens, which is why every step
   * carries a route: it starts wherever you happen to be and walks you out to
   * the organisation list, into a meeting, through the wizard and back to
   * Preppy. Steps needing a meeting are skipped for a user who has none yet.
   */
  {
    id: 'tour',
    title: 'Show me around AdvicePrep',
    category: 'Getting started',
    minutes: '90 sec',
    scopes: ['org-list', 'calls', 'prep', 'consolidated', 'settings'],
    body: 'A ninety-second flight around everything you will touch every week — the organisation list, a meeting’s status board, the four-step wizard, where exports come out, plus Settings and me.',
    steps: [
      {
        anchor: 'nav-advisoryai',
        route: '/adviceprep',
        text: 'AdvicePrep lives in the sidebar under Advisory. That is always your way back home.',
      },
      {
        anchor: 'org-actions',
        route: '/adviceprep',
        // Left to right, matching what is on screen. Hedged on Add Xero
        // Connection because it needs the `addPlatform` permission, so this
        // same sentence is read by people who cannot see it.
        text: 'Consolidated Analysis runs one prep across several organisations at once. Settings is AdvicePrep’s own — avatar, voice, agenda templates and theme colours, separate from the app-wide Settings below. Refresh re-reads the organisation list in place, which is quicker than reloading the page. Add Xero Connection brings a new organisation in, if you have the rights to.',
      },
      {
        anchor: 'org-table',
        route: '/adviceprep',
        advanceWhen: 'anchorClicked',
        clickAnchor: 'org-open',
        waitOptional: true,
        text: 'Every organisation you have connected lives here, with how fresh its data is. Click one to see its meetings.',
        waitText: 'Every organisation you have connected lives here, with how fresh its data is. Open one and I will follow you in — or press Next and I will pick one for you.',
      },
      {
        anchor: 'stats-row',
        route: '/adviceprep/org/:orgId/calls',
        text: 'Open an organisation and you get its status board — what is scheduled, what needs prep, what is ready to go.',
      },
      {
        anchor: 'internal-memory',
        route: '/adviceprep/org/:orgId/calls',
        text: 'Industry and Profile is what I know about the business — the industry it works in, how it operates, the history and sensitivities I should factor into the analysis. It stays with your team and is never said aloud to the client.',
      },
      {
        id: 'no-meeting',
        anchor: 'calls-list',
        route: '/adviceprep/org/:orgId/calls',
        onlyWhen: 'noMeeting',
        text: 'There are no meetings on this organisation yet, and the next stop is the prep itself. Shall we create one now, or would you rather I showed you round a sample first?',
        choices: [
          { label: 'Create one with me', goTo: 'create-meeting' },
          { label: 'Show me a sample', goTo: 'prep-stepper', sample: true, secondary: true },
        ],
      },
      {
        id: 'create-meeting',
        anchor: 'add-meeting',
        route: '/adviceprep/org/:orgId/calls',
        onlyWhen: 'noMeeting',
        advanceWhen: 'meetingCreated',
        handOff: 'generate',
        text: 'Add meeting creates the prep and opens it on the first step. Go ahead and click it — and once we are inside, we will do the prep together rather than me talking you past it.',
        waitText: 'Go on then — Add meeting. It creates a real prep on this organisation and opens it, and I will meet you inside to run through it start to finish.',
      },
      {
        id: 'prep-stepper',
        anchor: 'stepper',
        route: '/adviceprep/org/:orgId/report/:callId',
        step: PREP_STEP.agenda,
        text: 'Open a meeting and there are four steps — Agenda, Data, AI Analysis & Review, Generate Outputs. It saves as you go, and you can jump between them.',
      },
      {
        anchor: 'outputs',
        route: '/adviceprep/org/:orgId/report/:callId',
        step: PREP_STEP.outputs,
        needsAnalysis: true,
        text: 'Step 4 is where the pack comes out — PDF, slide deck, email summary, and a video summary if you have set up an avatar.',
      },
      {
        anchor: 'preppy-dock',
        text: 'And I am Preppy. Ask me about any open report, browse guides, raise a support ticket or send feedback. I float on the right edge, and you can drag me anywhere along it.',
        sampleText: 'And I am Preppy. Ask me about any open report, browse guides, raise a support ticket or send feedback — I float on the right edge, and you can drag me anywhere along it. Have a proper look round this sample prep while you are here: nothing in it is saved, and none of it belongs to a real organisation. When you want the real thing, Add meeting on any organisation starts one.',
      },
    ],
  },

  {
    id: 'connect',
    title: 'Connect a client’s Xero',
    category: 'Setup',
    minutes: '1 min',
    scopes: ['org-list', 'calls', 'prep', 'consolidated', 'settings'],
    body: 'Connect a client’s Xero without leaving AdvicePrep. The button on the organisation list opens Xero directly, and once the client has authorised it their organisations appear in the table ready to prep.',
    steps: [
      {
        // The button, not the Platform page. This used to walk people over to
        // Integrations, which was the only place the connection could be made
        // — it is not any more, and sending them off AdvicePrep to do a thing
        // AdvicePrep can do is a tour of the old way round.
        anchor: 'add-xero',
        route: '/adviceprep',
        // Waits for the real click rather than a Next press. The button opens a
        // window on another origin, so this is the last moment the walkthrough
        // can be sure of anything — after it, all we know is that they went.
        advanceWhen: 'anchorClicked',
        text: 'Add Xero Connection opens Xero in a separate window. Go ahead and click it — I will wait. You need the right to add connections to see this button.',
        waitText: 'Waiting on that click. When Xero opens, sign in as the client, choose the organisations to share and authorise it — that part happens outside the app, so take your time and come back to this tab.',
      },
      {
        anchor: 'org-table',
        route: '/adviceprep',
        text: 'Come back here when Xero is done and refresh the page. Each organisation the client authorised appears in this table, and its refresh status tells you whether the data behind a prep is current yet — a first sync takes a few minutes.',
      },
      {
        anchor: 'nav-platform',
        text: 'The same connection shows up under Integrations, because one connection serves every product that needs the ledger. Nothing to do there — it is where you would go to disconnect it or check why a sync failed.',
      },
    ],
  },

  /**
   * The coached one: you drive, Preppy waits.
   *
   * Every other guide narrates while you press Next. This one describes what to
   * do and then advances by itself once you have done it — which is why it can
   * cover a real prep end to end, including the minutes the analysis takes.
   *
   * The waiting conditions deliberately watch the wizard's own position rather
   * than re-checking required fields. The page already refuses to continue
   * without a meeting time, or without a period and reports; a second copy of
   * that validation here would be one more thing to keep in step.
   */
  {
    id: 'generate',
    title: 'Do a prep with me, start to finish',
    category: 'Meeting prep',
    minutes: 'As long as it takes',
    scopes: ['prep'],
    needsRealCall: true,
    body: 'Not a tour — we do it together on a real meeting. I will point at each step, wait while you fill it in, and pick up again on my own. The analysis in the middle takes a few minutes; I will tell you when it lands.',
    steps: [
      {
        anchor: 'step-body',
        step: PREP_STEP.agenda,
        advanceWhen: 'wizardAdvanced',
        text: 'Step 1, Agenda. Set the meeting time — that one is required — and add the items you want covered. The analysis is scoped to these, so the sharper they are the sharper the findings. Hit Continue when you are happy.',
        waitText: 'Take your time on the agenda. Set the meeting time, add your items, and hit Continue — I will pick up from there.',
      },
      {
        anchor: 'step-body',
        step: PREP_STEP.data,
        advanceWhen: 'wizardAdvanced',
        text: 'Step 2, Data. Choose the period and tick the reports to extract — both are required. Continue when that looks right.',
        waitText: 'Still with you. Pick the period and the reports you want, then Continue.',
        modalText: 'Worth a read before you commit: this is everything the analysis will be built from. Confirm and it starts running.',
      },
      {
        anchor: 'step-body',
        step: PREP_STEP.report,
        advanceWhen: 'analysisReady',
        text: 'Step 3, AI Analysis & Review. This runs on the server and takes a few minutes.',
        waitText: 'The analysis is running now. It takes a few minutes and you do not need to watch it — go and do something else, I will speak up the moment it lands.',
      },
      {
        anchor: 'findings',
        step: PREP_STEP.report,
        needsAnalysis: true,
        text: 'There they are. Findings sit under the agenda item each one answers. Rewrite anything in your own words — what you change here teaches the next draft for this client.',
      },
      {
        anchor: 'outputs',
        step: PREP_STEP.outputs,
        text: 'Step 4, Generate Outputs. Choose your export mix — each takes half a minute or so, and you can trigger them in parallel. That is the whole loop.',
      },
    ],
  },

  {
    id: 'analysis',
    title: 'Refining your Analysis',
    category: 'Analysis',
    minutes: '2 min',
    scopes: ['prep'],
    needsAnalysis: true,
    needsRealCall: true,
    body: 'The analysis ranks findings by materiality, not by what your client will actually care about — that judgement is yours. Pick the three you will say out loud, rewrite them in your own words, and the rest still ship in the pack.',
    steps: [
      {
        anchor: 'findings',
        step: PREP_STEP.report,
        needsAnalysis: true,
        text: 'Findings sit under the agenda item they answer. Nobody absorbs nine of them, so choose three — usually margin, cash, and one forward-looking. Not sure which three? Stop me and ask, on the Analysis tab.',
      },
      {
        id: 'edit-findings',
        anchor: 'finding-edit',
        step: PREP_STEP.report,
        needsAnalysis: true,
        highlightAll: true,
        allowInteraction: true,
        advanceWhen: 'anchorClicked',
        waitOptional: true,
        text: 'Each agenda item has its own Edit — open whichever finding you want to change and I will follow you in. What you write there teaches the next draft for this client, so it is worth putting in your own words rather than accepting mine.',
      },
      {
        id: 'edit-wait',
        anchor: 'findings',
        focusAnchor: 'finding-editing',
        step: PREP_STEP.report,
        needsAnalysis: true,
        advanceWhen: 'findingsSaved',
        allowInteraction: true,
        waitOptional: true,
        nextLabel: 'Skip',
        text: 'Rewrite what you want changed, then Save changes.',
        waitText: 'This is the item you opened. Rewrite what you want changed and press Save changes — I will wait here and pick up once it has saved. Nothing to change after all? Skip.',
        modalText: 'That is the confirmation: it lists exactly what is about to change. Confirm it and the findings are saved.',
      },
      {
        id: 'analysis-outputs',
        anchor: 'outputs',
        step: PREP_STEP.outputs,
        needsAnalysis: true,
        text: 'And on to Generate Outputs, where your summary and notes become the client pack.',
      },
    ],
  },

  {
    id: 'consolidated',
    title: 'Run one analysis across a group of organisations',
    category: 'Consolidated',
    minutes: '1 min',
    scopes: ['org-list', 'consolidated'],
    body: 'For franchisees, subsidiaries, or any portfolio under the same owner. Group them once, analyse them together, and get a portfolio view rather than a stack of separate packs.',
    steps: [
      {
        anchor: 'group-list',
        route: '/adviceprep/consolidated',
        text: 'Groups live here — each row is one portfolio you have set up. Create one by picking the organisations that belong together.',
      },
      {
        anchor: 'stats-row',
        route: '/adviceprep/consolidated',
        text: 'The board above rolls the whole set up, so you can see at a glance which portfolios need work.',
      },
      {
        // Needs the detail route: the stepper lives there, not on the list. A
        // consolidated analysis is an advisory call, so `:callId` is its groupId.
        anchor: 'stepper',
        route: '/adviceprep/consolidated/:callId',
        text: 'Open a group and it runs the same four steps as a single meeting — the difference is that the findings are portfolio-wide, showing what is systemic across the group rather than isolated to one organisation.',
      },
    ],
  },
]

/* ── help articles (links out, never rendered in-panel) ──────────────────── */

// HELP_ARTICLES moved to ./helpArticles.ts when they gained bodies — six
// titles fit here, eleven articles do not.

/* ── support ────────────────────────────────────────────────────────────── */

/**
 * The five areas (spec §4).
 *
 * Stored and filterable, unlike feedback's feature keys — but the server does
 * not validate against this list, so a sixth ships as a frontend deploy rather
 * than a coordinated release. This array is the whole definition.
 */
export const SUPPORT_AREAS = [
  'Integrations and source data',
  'Analysis and findings',
  'Exports and packs',
  'Billing and plan',
  'Something else',
]

export const TICKET_STATUS_LABEL: Record<TicketStatusValue, string> = {
  open: 'Open',
  reviewing: 'Reviewing',
  awaiting: 'Awaiting you',
  closed: 'Closed',
  unknown: 'Updated',
}

export const TICKET_STATUS_EXPLAINER: Record<TicketStatusValue, string> = {
  open: 'raised and queued. Nobody has picked it up yet.',
  reviewing: 'somebody is looking at it now.',
  awaiting: 'support has replied and is waiting on you. Answer below.',
  // Not "reply to reopen": the server refuses answers on a closed ticket.
  closed: 'closed. Raise a new ticket if it comes back.',
  unknown: 'support has changed it. Check back shortly.',
}

/** What the console shows admins, where "awaiting you" is about someone else. */
export const TICKET_STATUS_ADMIN_LABEL: Record<TicketStatusValue, string> = {
  open: 'Open',
  reviewing: 'Reviewing',
  awaiting: 'Awaiting user',
  closed: 'Closed',
  unknown: 'Unknown',
}

/* ── feedback ───────────────────────────────────────────────────────────── */

export const RATABLE_FEATURES: RatableFeature[] = [
  { key: 'agenda', name: 'Agenda builder', hint: 'Templates, items, meeting time' },
  { key: 'data', name: 'Source data', hint: 'Ledger pulls and source reports' },
  { key: 'analysis', name: 'AI analysis', hint: 'Findings, summary, materiality' },
  { key: 'review', name: 'Review and notes', hint: 'Generated wording you can edit' },
  { key: 'outputs', name: 'Outputs', hint: 'PDF pack, slides, video report' },
  { key: 'consolidated', name: 'Consolidated prep', hint: 'Several organisations in one pass' },
]

export const RATING_REPLY = [
  '',
  'Sorry to hear it',
  'Noted',
  'Fair enough',
  'Glad it is working',
  'Delighted to hear it',
]
